import { createNotification } from '@fastlessjs/backoffice-layer/dist/components/atoms';
import { currentOrganization } from '@fastlessjs/backoffice-layer/dist/hooks';
import {
  request,
  RequestErrorResponse,
} from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { navigate } from 'hookrouter-react';

import { t, pickRuleUserMessage } from '../../config/translations';
import { resetReturns, useReturnStore } from '../../store';
import { APIOptions } from '../../types';
import { Return } from '../types';

class ReturnSubmissionInProgressError extends Error {
  constructor() {
    super('Return submission already in progress');
    this.name = 'ReturnSubmissionInProgressError';
  }
}

/** Runtime field from API; optional on type until backoffice-layer package is republished. */
type RequestErrorMaybeLocalized = RequestErrorResponse & {
  localizedMessages?: { fr?: string; en?: string };
};

type CreateSuiviResponse = Return & {
  data?: Return[];
};

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40;

debug.registerComponent('returns-hooks');

const unwrapReturnPayload = (payload: CreateSuiviResponse | Return): Return => {
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as Return).items)
  ) {
    return payload as Return;
  }
  const nested = (payload as CreateSuiviResponse)?.data?.[0];
  if (nested) {
    return nested;
  }
  return payload as Return;
};

const createReturn = async (
  payload: Partial<Return>,
  options?: APIOptions,
): Promise<Return> => {
  debug.log('returns-hooks', `Creating return request`);

  try {
    const result = await request(`/returns/EPI/`, {
      method: 'post',
      options: { body: payload, ...options },
    });
    debug.log('returns-hooks', `Return suivi created`);
    return unwrapReturnPayload(result);
  } catch (error) {
    debug.error('returns-hooks', `Error creating return: ${error}`);
    throw error;
  }
};

const getSuiviStatus = async (suiviId: string, options?: APIOptions): Promise<Return> => {
  const result = await request(`/returns/suivi/${suiviId}`, {
    method: 'get',
    options,
  });
  const row = (result as CreateSuiviResponse)?.data?.[0] || result;
  return {
    ...(row as Return),
    suiviId: (row as Return)?.suiviId || suiviId,
    returnStatus: (row as Return)?.returnStatus,
    nsui: (row as Return)?.eurekaNsui
      ? String((row as Return).eurekaNsui)
      : (row as Return)?.nsui,
  };
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const waitForConfirmedSuivi = async (
  suiviId: string,
  options?: APIOptions,
  attempt = 0,
): Promise<Return> => {
  const suivi = await getSuiviStatus(suiviId, options);
  if (suivi.returnStatus === 'confirmed') {
    return suivi;
  }
  if (suivi.returnStatus === 'failed') {
    throw new Error(suivi.errorMessage || 'Return processing failed');
  }
  if (attempt >= POLL_MAX_ATTEMPTS - 1) {
    return suivi;
  }
  await sleep(POLL_INTERVAL_MS);
  return waitForConfirmedSuivi(suiviId, options, attempt + 1);
};

const useCreateReturn = (options?: APIOptions) => {
  debug.trackAccess('useCreateReturn', 'hook execution');
  debug.log('useCreateReturn', `Hook initialized`);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (returnOrder: Partial<Return>) => {
      debug.log('useCreateReturn', `Mutation executing with return data`);
      const { beginReturnSubmission, endReturnSubmission } = useReturnStore.getState();
      if (!beginReturnSubmission()) {
        throw new ReturnSubmissionInProgressError();
      }

      try {
        return await createReturn(returnOrder, options);
      } catch (error) {
        endReturnSubmission();
        throw error;
      }
    },
    onError: (error) => {
      if (error instanceof ReturnSubmissionInProgressError) {
        debug.warn('useCreateReturn', 'Ignored duplicate return submission');
        return;
      }
      debug.error('useCreateReturn', `Mutation error: ${error}`);
      const rawMessage =
        error instanceof RequestErrorResponse ? error.message : `${error}`;
      const displayMessage =
        error instanceof RequestErrorResponse
          ? pickRuleUserMessage(
              rawMessage,
              (error as RequestErrorMaybeLocalized).localizedMessages,
            )
          : rawMessage;
      const errorName = error instanceof RequestErrorResponse ? error.name : '';
      const isDuplicateError =
        errorName === 'DuplicateError' ||
        displayMessage.includes('DuplicateError') ||
        displayMessage.includes('409') ||
        displayMessage.includes('already exist');

      if (isDuplicateError) {
        createNotification(
          'error',
          t('notification.return.error'),
          t('notification.return.error.duplicate'),
        );
      } else {
        createNotification('error', t('notification.return.error'), displayMessage);
      }
    },
    onSuccess: async (payload) => {
      const { endReturnSubmission } = useReturnStore.getState();
      try {
        const record = unwrapReturnPayload(payload);
        resetReturns();
        queryClient.invalidateQueries({ queryKey: ['suivis'] });

        if (record.returnStatus === 'pending' && record.suiviId) {
          createNotification(
            'info',
            t('notification.return.success.title'),
            t('notification.return.pending.description'),
          );

          try {
            const confirmed = await waitForConfirmedSuivi(record.suiviId, options);
            if (confirmed.returnStatus === 'confirmed') {
              createNotification(
                'success',
                t('notification.return.success.title'),
                t('notification.return.success.description'),
                { duration: 0 },
              );
            }
          } catch (error) {
            debug.error(
              'useCreateReturn',
              `Return confirmation polling failed: ${error}`,
            );
            createNotification(
              'warning',
              t('notification.return.error'),
              t('notification.return.pending.stillProcessing'),
            );
          }
        } else {
          createNotification(
            'success',
            t('notification.return.success.title'),
            t('notification.return.success.description'),
            { duration: 0 },
          );
        }

        navigate(`/${currentOrganization()?.organizationId}/suivi`);
      } finally {
        endReturnSubmission();
      }
    },
  });
};

export { useCreateReturn };

debug.markInitialized('returns-hooks');
